import { FC } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { primary, black } from "@/theme/colors";
import { TWeeklyDayData } from "@/infrastructure/adapters/study-time-tracking/study-time-tracking.types";
import dayjs from "dayjs";

const BAR_MAX_HEIGHT = 80;

const toMondayFirstIndex = (dayjsDay: number): number => (dayjsDay === 0 ? 6 : dayjsDay - 1);

type Props = {
  days: TWeeklyDayData[];
  dailyTargetMinutes: number;
};

export const WeeklyBarChart: FC<Props> = ({ days, dailyTargetMinutes }) => {
  const { t } = useTranslation();
  const maxMinutes = Math.max(...days.map((d) => d.minutes), dailyTargetMinutes, 1);
  const today = dayjs().format("YYYY-MM-DD");

  /** Day labels array from i18n: index 0=Mon .. 6=Sun */
  const dayLabels: string[] = (t("dashboard.weeklyLearning.dayLabels", { returnObjects: true }) as string[]) ?? [];

  return (
    <Stack direction="row" justifyContent="space-around" alignItems="flex-end" sx={{ height: BAR_MAX_HEIGHT + 50, px: "10px", py: "4px" }}>
      {days.map((day) => {
        const isToday = day.date === today;
        const barHeight = (day.minutes / maxMinutes) * BAR_MAX_HEIGHT;
        const labelIndex = toMondayFirstIndex(dayjs(day.date).day());

        return (
          <Stack key={day.date} alignItems="center" spacing={0.5} minWidth={24}>
            {/* Minutes label above bar */}
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 500,
                lineHeight: "150%",
                color: black[10],
              }}
            >
              {day.minutes}
            </Typography>
            {/* Bar */}
            <Box
              sx={{
                width: 24,
                height: Math.max(barHeight, day.minutes > 0 ? 4 : 0),
                bgcolor: isToday ? primary[500] : black[99],
                borderRadius: "4px 4px 0 0",
                borderBottom: `1px solid ${isToday ? primary[500] : black[99]}`,
                transition: "height 0.3s ease",
              }}
            />
            {/* Day label */}
            <Typography variant="body14Semi" color={isToday ? primary[500] : black[70]} minWidth={24} textAlign="center">
              {dayLabels[labelIndex] ?? ""}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};
